import React, { useState, useMemo } from 'react';
import {
  ISO27001_ANNEX_A_CATALOG,
  getAnnexAControlsByDomain,
  type AnnexAControl,
  type ControlStatus,
} from '../../data/iso27001AnnexACatalog';
import { computeAnnexAScore } from '../../utils/iso27001Score';
import { useI18n } from '../../i18n';
import { ChevronDown, ChevronRight, AlertTriangle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { buildGovernanceDeepLink } from '../../utils/governanceLinks';
import { componentScope } from '../../lib/cssScope'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'

const DOMAIN_ORDER: AnnexAControl['domain'][] = ['Organizational', 'People', 'Physical', 'Technological'];
const STATUS_OPTIONS: ControlStatus[] = ['IMPLEMENTED', 'PARTIAL', 'NOT_IMPLEMENTED', 'N_A'];

const STATUS_LABELS: Record<ControlStatus, string> = {
  IMPLEMENTED: 'Implemented',
  PARTIAL: 'Partial',
  NOT_IMPLEMENTED: 'Not Implemented',
  N_A: 'N/A',
};

interface AnnexAControlsPanelProps {
  initialStatuses?: Record<string, ControlStatus>;
  onStatusChange?: (controlCode: string, status: ControlStatus) => void;
  onSave?: (statuses: Record<string, ControlStatus>) => Promise<void>;
  disabled?: boolean;
}

export default function AnnexAControlsPanel({
  initialStatuses = {},
  onStatusChange,
  onSave,
  disabled = false,
}: AnnexAControlsPanelProps) {
  const { t, locale } = useI18n();
  const [statuses, setStatuses] = useState<Record<string, ControlStatus>>(initialStatuses);
  React.useEffect(() => {
    if (initialStatuses && Object.keys(initialStatuses).length > 0) {
      setStatuses((prev) => ({ ...prev, ...initialStatuses }));
    }
  }, [initialStatuses]);
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set(['Organizational']));
  const [filterDomain, setFilterDomain] = useState<AnnexAControl['domain'] | 'all'>('all');
  const [isSaving, setIsSaving] = useState(false);

  const handleStatusChange = (controlCode: string, status: ControlStatus | '') => {
    if (disabled) return;
    if (status === '') {
      setStatuses((prev) => {
        const next = { ...prev };
        delete next[controlCode];
        return next;
      });
      return;
    }
    const next = { ...statuses, [controlCode]: status };
    setStatuses(next);
    onStatusChange?.(controlCode, status);
  };

  const handleSave = async () => {
    if (disabled || isSaving || !onSave) return;
    setIsSaving(true);
    try {
      await onSave(statuses);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleDomain = (domain: string) => {
    setExpandedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  };

  const controlsByDomain = useMemo(() => {
    const list = filterDomain === 'all'
      ? DOMAIN_ORDER.flatMap((d) => getAnnexAControlsByDomain(d))
      : getAnnexAControlsByDomain(filterDomain);
    return list;
  }, [filterDomain]);

  const stats = useMemo(() => {
    const total = ISO27001_ANNEX_A_CATALOG.length;
    const { score, breakdown } = computeAnnexAScore(statuses);
    return {
      total,
      implemented: breakdown.implemented,
      partial: breakdown.partial,
      notImpl: breakdown.notImplemented,
      na: breakdown.na,
      applicable: breakdown.totalNonNA,
      score,
    };
  }, [statuses]);

  const getTitle = (c: AnnexAControl) => (locale === 'ru' ? c.title.ru : locale === 'es' ? c.title.es : c.title.en);
  const getDesc = (c: AnnexAControl) => (locale === 'ru' ? c.description.ru : locale === 'es' ? c.description.es : c.description.en);
  const getRec = (c: AnnexAControl) => {
    const r = c.recommendations.CRITICAL || c.recommendations.MAJOR || c.recommendations.MODERATE;
    if (!r) return null;
    return locale === 'ru' ? r.ru : locale === 'es' ? r.es : r.en;
  };

  return (
    <div className={`${componentScope('AnnexAControlsPanel')} space-y-4`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('compliance.iso27001.annexA.title', 'ISO 27001:2022 Annex A Controls')}
          </h3>
          <p className="text-sm text-gray-500">
            {t('compliance.iso27001.annexA.subtitle', '93 controls • Status: Implemented / Partial / Not Implemented / N/A')}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Select<AnnexAControl['domain'] | 'all'>
            options={[
              { value: 'all', label: t('compliance.iso27001.annexA.allDomains', 'All domains') },
              ...DOMAIN_ORDER.map((d) => ({ value: d, label: d })),
            ]}
            value={filterDomain}
            onChange={(v) => v != null && setFilterDomain(v)}
            placeholder={t('compliance.iso27001.annexA.allDomains', 'All domains')}
            minWidth="12rem"
          />
          {onSave && (
            <Button type="button" color="primary" size="small" onClick={handleSave} disabled={disabled || isSaving}>
              {isSaving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
        <div className="flex flex-wrap gap-6 text-sm">
          <span>
            <strong>{stats.implemented}</strong> {t('compliance.iso27001.annexA.implemented', 'Implemented')}
          </span>
          <span>
            <strong>{stats.partial}</strong> {t('compliance.iso27001.annexA.partial', 'Partial')}
          </span>
          <span>
            <strong>{stats.notImpl}</strong> {t('compliance.iso27001.annexA.notImplemented', 'Not Implemented')}
          </span>
          <span>
            <strong>{stats.na}</strong> N/A
          </span>
          <span className="ml-auto font-medium">
            {t('compliance.iso27001.annexA.score', 'Annex A Score')}: <strong>{stats.score}%</strong>
          </span>
        </div>
      </div>

      <div className="space-y-2">
        {filterDomain === 'all'
          ? DOMAIN_ORDER.map((domain) => {
              const domainControls = getAnnexAControlsByDomain(domain);
              const isExpanded = expandedDomains.has(domain);
              return (
                <div key={domain} className="rounded-lg border border-gray-200 dark:border-gray-700">
                  <Button
                    type="button"
                    color="outline"
                    onClick={() => toggleDomain(domain)}
                    className="w-full !justify-between rounded-none !border-x-0 !border-t-0 !border-b-gray-200 !bg-transparent !px-4 !py-3 !text-left !font-medium !text-gray-900 !shadow-none dark:!border-b-gray-700 dark:!text-gray-100"
                  >
                    <span className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      {domain} ({domainControls.length})
                    </span>
                  </Button>
                  {isExpanded && (
                    <div className="border-t border-gray-200 dark:border-gray-700">
                      {domainControls.map((control) => (
                        <ControlRow
                          key={control.controlCode}
                          control={control}
                          status={statuses[control.controlCode] || null}
                          onStatusChange={handleStatusChange}
                          getTitle={getTitle}
                          getDesc={getDesc}
                          getRec={getRec}
                          disabled={disabled}
                          locale={locale}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          : controlsByDomain.map((control) => (
              <ControlRow
                key={control.controlCode}
                control={control}
                status={statuses[control.controlCode] || null}
                onStatusChange={handleStatusChange}
                getTitle={getTitle}
                getDesc={getDesc}
                getRec={getRec}
                disabled={disabled}
                locale={locale}
              />
            ))}
      </div>
    </div>
  );
}

function ControlRow({
  control,
  status,
  onStatusChange,
  getTitle,
  getDesc,
  getRec,
  disabled,
  locale,
}: {
  control: AnnexAControl;
  status: ControlStatus | null;
  onStatusChange: (code: string, s: ControlStatus | '') => void;
  getTitle: (c: AnnexAControl) => string;
  getDesc: (c: AnnexAControl) => string;
  getRec: (c: AnnexAControl) => string | null;
  disabled: boolean;
  locale: string;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const code = control.controlCode.replace('ISO27001_', '');
  return (
    <div className="border-b border-gray-100 last:border-b-0 dark:border-gray-700/50">
      <div className="flex flex-wrap items-start gap-4 px-4 py-3">
        <div className="min-w-0 flex-1">
          <Button
            type="button"
            color="outline"
            onClick={() => setShowDetails(!showDetails)}
            className="!border-transparent !bg-transparent !p-0 !text-left !font-medium !text-gray-900 !shadow-none hover:!underline dark:!text-gray-100"
          >
            {code}: {getTitle(control)}
          </Button>
          {control.vendorImpact.applies && (
            <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">
              Vendor impact
            </span>
          )}
        </div>
        <Select<string>
          options={[
            { value: '', label: '—' },
            ...STATUS_OPTIONS.map((s) => ({ value: s, label: STATUS_LABELS[s] })),
          ]}
          value={status ?? ''}
          onChange={(v) => onStatusChange(control.controlCode, (v ?? '') as ControlStatus | '')}
          disabled={disabled}
          minWidth="9rem"
        />
      </div>
      {showDetails && (
        <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3 text-sm dark:border-gray-700/50 dark:bg-gray-800/30">
          <p className="text-gray-600 dark:text-gray-400">{getDesc(control)}</p>
          {control.evidence.length > 0 && (
            <p className="mt-2 text-gray-500">
              Evidence: {control.evidence.map((e) => e.type).join(', ')}
            </p>
          )}
          {(control.governanceLinks.policies.length > 0 || control.governanceLinks.processes.length > 0 || control.governanceLinks.roles.length > 0) && (
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="text-xs font-medium text-gray-500">Governance:</span>
              {control.governanceLinks.policies.map((key) => (
                <Link
                  key={`p-${key}`}
                  to={buildGovernanceDeepLink({ tab: 'policies', search: key })}
                  className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-200"
                >
                  <ExternalLink className="h-3 w-3" /> Policy
                </Link>
              ))}
              {control.governanceLinks.processes.map((key) => (
                <Link
                  key={`proc-${key}`}
                  to={buildGovernanceDeepLink({ tab: 'processes', search: key })}
                  className="inline-flex items-center gap-1 rounded bg-green-50 px-2 py-0.5 text-xs text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-200"
                >
                  <ExternalLink className="h-3 w-3" /> Process
                </Link>
              ))}
              {control.governanceLinks.roles.map((key) => (
                <Link
                  key={`r-${key}`}
                  to={buildGovernanceDeepLink({ tab: 'roles', search: key })}
                  className="inline-flex items-center gap-1 rounded bg-purple-50 px-2 py-0.5 text-xs text-purple-700 hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-200"
                >
                  <ExternalLink className="h-3 w-3" /> Role
                </Link>
              ))}
            </div>
          )}
          {(status === 'PARTIAL' || status === 'NOT_IMPLEMENTED') && getRec(control) && (
            <div className="mt-2 flex items-start gap-2 rounded-md bg-amber-50 p-2 dark:bg-amber-900/20">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-amber-800 dark:text-amber-200">{getRec(control)}</p>
            </div>
          )}
          {(control.crossMapping.ENS_ES.length > 0 || control.crossMapping.GDPR.length > 0 || control.crossMapping.SOC2.length > 0 || control.crossMapping.NIS2.length > 0) && (
            <p className="mt-2 text-xs text-gray-500">
              Mapped to: {[
                ...control.crossMapping.ENS_ES.map((x) => `ENS ${x}`),
                ...control.crossMapping.GDPR.map((x) => `GDPR ${x}`),
                ...control.crossMapping.SOC2.map((x) => `SOC2 ${x}`),
                ...control.crossMapping.NIS2.map((x) => `NIS2 ${x}`),
              ].join(', ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
